import React, { useState, useEffect, useRef } from "react";
import { Button, Input, Space, Table, message, Modal } from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AddRole from "../rolesManager/addRole";
import EditRole from "../rolesManager/editRole";
import "./rolesV2.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const apiUrl = process.env.REACT_APP_API_Roles;

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? dateStr : d.toLocaleString();
};

const RolesV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [roles, setRoles] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [roleId, setRoleId] = useState(null);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  useEffect(() => {
    fetchData();
  }, [open, editModalVisible]);

  const fetchData = async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Failed to fetch roles");
      const json = await response.json();
      const data = json.data ?? json.body ?? [];
      const allRoles = Array.isArray(data) ? data : [];
      const filteredRoles = allRoles.filter(
        (r) =>
          r.id &&
          ![1, 2, 3].includes(r.id) &&
          (r.is_tester === 1 || r.is_tester === true)
      );
      setRoles(filteredRoles);
    } catch (error) {
      console.error("Error fetching roles:", error);
      messageApi.error("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="roles-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="roles-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "roles-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleEditClick = (record) => {
    setEditModalVisible(true);
    setRoleId(record.id);
  };

  const handleDeleteClick = (record) => {
    setSelectedRecord(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${apiUrl}/${selectedRecord.id}`, {
        method: "DELETE",
      });
      const json = await response.json();

      if (response.ok) {
        messageApi.success("Role deleted successfully");
        setRoles(roles.filter((r) => r.id !== selectedRecord.id));
      } else {
        const errorMsg = json?.errorMsg || json?.message || "Failed to delete role";
        messageApi.error(errorMsg);
      }
    } catch (error) {
      messageApi.error("Failed to delete role");
    }
    setDeleteModalVisible(false);
  };

  const handleAddClick = () => setOpen(true);

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      sorter: (a, b) => (a.id || 0) - (b.id || 0),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      ...getColumnSearchProps("title"),
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Countries",
      dataIndex: "country_count",
      key: "country_count",
      width: 120,
      render: (val) => {
        const n = Number(val) || 0;
        return n === 0 ? "—" : `${n} countr${n === 1 ? "y" : "ies"}`;
      },
      sorter: (a, b) => (Number(a.country_count) || 0) - (Number(b.country_count) || 0),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "User DB",
      dataIndex: "user_db",
      key: "user_db",
      width: 100,
      render: (val) => val ? String(val).charAt(0).toUpperCase() + String(val).slice(1) : "—",
      sorter: (a, b) => (a.user_db || "").localeCompare(b.user_db || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Is Tester",
      dataIndex: "is_tester",
      key: "is_tester",
      width: 100,
      render: (val) => (val === 1 || val === true ? "Yes" : "No"),
      sorter: (a, b) => (a.is_tester ? 1 : 0) - (b.is_tester ? 1 : 0),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (val) => formatDate(val),
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 160,
      render: (val) => formatDate(val),
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => {
        const isSystemRole = [4, 7, 9].includes(record.id);
        return (
          <div className="roles-v2-actions">
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record)}
              className="roles-v2-btn-edit"
              disabled={isSystemRole}
            >
              Edit
            </Button>
            <Button
              type="default"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteClick(record)}
              danger
              className="roles-v2-btn-delete"
              disabled={isSystemRole}
            >
              Delete
            </Button>
        </div>
        );
      },
    },
  ];

  return (
    <div className="roles-v2">
      {contextHolder}
      <div className="roles-v2-header">
        <h1 className="roles-v2-title">All Roles</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddClick}
          className="roles-v2-add-btn"
        >
          Add Role
        </Button>
      </div>

      <div className="roles-v2-card">
        <Table
          columns={columns}
          dataSource={roles}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1100 }}
          locale={{ emptyText: loading ? "Loading..." : "No roles yet" }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total) => `Total ${total} roles`,
          }}
          className="roles-v2-table"
        />
      </div>

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        className="roles-v2-delete-modal"
        width={420}
      >
        <p className="roles-v2-delete-text">
          Are you sure you want to delete role <strong>{selectedRecord?.title}</strong>?
          This action cannot be undone. Roles with assigned users cannot be deleted.
        </p>
      </Modal>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        className="roles-v2-dialog"
        PaperProps={{
          className: "roles-v2-dialog-paper",
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AddRole setOpen={setOpen} />
      </Dialog>

      <Dialog
        open={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        className="roles-v2-dialog"
        PaperProps={{
          className: "roles-v2-dialog-paper",
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <EditRole setOpen={setEditModalVisible} id={roleId} />
      </Dialog>
    </div>
  );
};

export default RolesV2;
